import { useEffect, useRef, useState, useCallback } from "react";
import { Col, Container, Row, Spinner, Badge } from "react-bootstrap";
import { FULLINFO, LITEINFO, DOWNLOADVOLCSV } from "../../constants/endpoints";
import { toast } from "react-toastify";
import axios from "axios";
import ModalWait from "../../components/ModalWait/ModalWait";
import { FaChevronRight, FaChevronDown } from "react-icons/fa";
import { FaDownload } from "react-icons/fa6";
import { useTranslation } from "react-i18next";

// --- Componentes Reutilizáveis ---

/**
 * Componente para exibir os detalhes de um único volume.
 * @param {object} props - As propriedades do componente.
 * @param {object} props.volume - O objeto de dados do volume.
 */
const VolumeDetails = ({ volume }) => {
    if (!volume) return null;

    const formatDate = (dateString) => {
        if (!dateString) return "";
        try {
            return new Date(dateString).toLocaleDateString("pt-BR");
        } catch (e) {
            console.error("Erro ao formatar data:", e);
            return dateString; // Retorna a string original em caso de erro
        }
    };

    return (
        <div className="volume-details-card p-3 my-2" style={{ border: '1px solid #eee', borderRadius: '5px', backgroundColor: '#f9f9f9' }}>
            <Row>
                <Col sm={12} md={10}>
                    <h6 className="mb-1">
                        <strong>Product: </strong> {volume.sku || "N/A"} - {volume.skudescription || "Description Unavailable"}
                    </h6>
                </Col>
                <Col sm={12} md={2}>
                    <h6 className="mb-1">
                        <strong>Qty: </strong> <Badge bg="secondary">{volume.qtdsn || 0}</Badge>
                    </h6>
                </Col>
            </Row>
            <Row>
                <Col sm={12} md={4}>
                    <h6 className="mb-1">
                        <strong>Date: </strong> {formatDate(volume.LastUpdate)}
                    </h6>
                </Col>
                {volume.peso_kg ? (
                    <Col sm={6} md={4}>
                        <h6 className="mb-1">
                            <strong>Weight: </strong> {`${volume.peso_kg} kg`}
                        </h6>
                    </Col>
                ) : null}
            </Row>
            {(volume.comprimento_cm > 0 && volume.largura_cm > 0 && volume.altura_cm > 0) && (
                <Row>
                    <Col sm={12} md={6}>
                        <h6 className="mb-1">
                            <strong>Dimensions (LxWxH): </strong> {`${volume.comprimento_cm} x ${volume.largura_cm} x ${volume.altura_cm} cm`}
                        </h6>
                    </Col>
                </Row>
            )}
        </div>
    );
};

/**
 * Componente para exibir os detalhes de um SN (Número de Série) inline.
 * @param {object} props - As propriedades do componente.
 * @param {object} props.sn - O objeto de dados do SN.
 */
const SnDetails = ({ sn }) => {
    if (!sn) return null;

    return (
        <div className="sn-details-card p-2 my-2 ms-4" style={{ border: '1px solid #d0e3ff', borderRadius: '5px', backgroundColor: '#f0f8ff' }}>
            <Row>
                <Col xs={12}><p className="mb-1"><strong>Product:</strong> {sn.sku || 'N/A'} - {sn.skudescription || 'N/A'}</p></Col>
            </Row>
            {sn.metadata && Object.keys(sn.metadata).length > 0 && (
                <>
                    <hr className="my-1" style={{ borderColor: '#b8daff' }} />
                    <Row>
                        {Object.entries(sn.metadata).map(([key, value]) => (
                            <Col xs={12} md={6} key={key}>
                                <p className="mb-1"><strong>{key}:</strong> {value}</p>
                            </Col>
                        ))}
                    </Row>
                </>
            )}
        </div>
    );
};

// --- Função de Chamada de API Centralizada ---

/**
 * Função centralizada para fazer chamadas de API.
 * @param {string} vol - O código do volume.
 * @returns {Promise<object|null>} Os dados do resultado ou null em caso de erro.
 */
const fetchFullInfo = async (vol) => {
    try {
        const response = await axios.get(`${FULLINFO}?vol=${vol}`, {
            headers: { "Content-Type": "application/json" },
            // maxBodyLength: Infinity, // Geralmente não é necessário para requisições pequenas como esta
        });

        if (response.status !== 200 || !response.data?.result) {
            throw new Error(response.data?.detail || "Falha ao obter dados");
        }
        return response.data.result;
    } catch (error) {
        let errorMessage = "Erro desconhecido ao obter informações do volume.";

        if (axios.isAxiosError(error)) {
            const apiDetails = error.response?.data?.detail;
            if (Array.isArray(apiDetails)) {
                apiDetails.forEach(msg => toast.error(msg));
                return null;
            } else if (apiDetails) {
                errorMessage = apiDetails;
            } else if (error.message) {
                errorMessage = error.message;
            }
        } else {
            errorMessage = error.message;
        }

        if (!Array.isArray(error?.response?.data?.detail)) { // Evita duplicar o toast se já foi exibido acima
            toast.error(errorMessage);
        }
        console.error("Erro em fetchFullInfo:", error);
        return null;
    }
};

/**
 * Função centralizada para fazer chamadas de API (apenas informação base do volume).
 * @param {string} vol - O código do volume.
 * @returns {Promise<object|null>} Os dados do resultado ou null em caso de erro.
 */
const fetchLiteInfo = async (vol) => {
    try {
        const response = await axios.get(`${LITEINFO}?vol=${vol}`, {
            headers: { "Content-Type": "application/json" },
        });

        if (response.status !== 200 || !response.data?.result) {
            throw new Error(response.data?.detail || "Falha ao obter dados");
        }
        return response.data.result;
    } catch (error) {
        let errorMessage = "Erro desconhecido ao obter informações leves do volume.";

        if (axios.isAxiosError(error)) {
            const apiDetails = error.response?.data?.detail;
            if (Array.isArray(apiDetails)) {
                apiDetails.forEach(msg => toast.error(msg));
                return null;
            } else if (apiDetails) {
                errorMessage = apiDetails;
            } else if (error.message) {
                errorMessage = error.message;
            }
        } else {
            errorMessage = error.message;
        }

        if (!Array.isArray(error?.response?.data?.detail)) { // Evita duplicar o toast se já foi exibido acima
            toast.error(errorMessage);
        }
        console.error("Erro em fetchLiteInfo:", error);
        return null;
    }
};

// --- Componente Recursivo para Renderizar Volumes ---

/**
 * Componente recursivo para renderizar a hierarquia de volumes.
 * @param {object} props - As propriedades do componente.
 * @param {object} props.volume - O objeto de volume atual (pode ser o pai).
 * @param {object} props.expandedNodes - Mapa de volumes expandidos.
 * @param {function} props.handleExpand - Função para expandir/recolher.
 * @param {object} props.nodesVisibility - Mapa que controla a visibilidade dos nós.
 * @param {object} props.loadingNodes - Mapa que controla o estado de loading de cada nó.
 * @param {function} props.handleShowSnDetails - Função para mostrar os detalhes de um SN.
 * @param {object} props.expandedSns - Mapa de SNs expandidos.
 */
const VolumeTree = ({ volume, expandedNodes, handleExpand, nodesVisibility, loadingNodes, handleSnClick, expandedSns }) => {
    if (!volume || !volume.children) return null;

    return (
        <ul style={{ listStyleType: 'none', paddingLeft: '15px' }}>
            {volume.children.map((child) => (
                <li key={child.RecordName} className="mb-1">
                    {child.RecordType === "VOLUME" ? ( // Se for um volume, renderiza como um nó expansível
                        <div className="volume-node">
                            <h5
                                className="d-flex align-items-center"
                                style={{ cursor: "pointer", marginBottom: "5px" }}
                                onClick={() => handleExpand(child.CodVolume)}
                            >
                                {loadingNodes[child.CodVolume] ? (
                                    <Spinner animation="border" size="sm" className="me-2" />
                                ) : nodesVisibility[child.CodVolume] ? (
                                    <FaChevronDown className="me-2" />
                                ) : (
                                    <FaChevronRight className="me-2" />
                                )}
                                <h5 style={{
                                    fontWeight: nodesVisibility[child.CodVolume] ? 'bold' : 'normal',
                                    color: nodesVisibility[child.CodVolume] ? '#0056b3' : 'inherit',
                                    marginBottom: '0px'
                                }}>
                                    {child.CodVolume}
                                </h5>
                            </h5>
                            {/* Renderiza os detalhes do volume expandido e, recursivamente, seus filhos */}
                            {nodesVisibility[child.CodVolume] && expandedNodes[child.CodVolume] && (
                                <div style={{ marginLeft: '10px', borderLeft: '2px solid #ddd', paddingLeft: '15px' }}>
                                    <VolumeDetails volume={expandedNodes[child.CodVolume]} />
                                    <VolumeTree
                                        volume={expandedNodes[child.CodVolume]}
                                        expandedNodes={expandedNodes}
                                        handleExpand={handleExpand}
                                        nodesVisibility={nodesVisibility}
                                        loadingNodes={loadingNodes}
                                        handleSnClick={handleSnClick}
                                        expandedSns={expandedSns}
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div>
                            <div
                                className="mb-1 mt-2 d-flex align-items-center"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleSnClick(child.RecordName)}>
                                <Badge bg="info" className="me-2">SN</Badge>
                                <h5 className="mb-0">{child.RecordName}</h5>
                            </div>
                            {expandedSns[child.RecordName] && <SnDetails sn={child} />}
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );
};


// --- Componente Principal ---

function PageQrCode() {
    // 1. Estados e Referências
    const [isLoadingInitial, setIsLoadingInitial] = useState(false); // Separado do ModalWait para clareza
    const [valuesMap, setValuesMap] = useState(null); // Usar 'null' para indicar ausência de dados
    const [expandedNodes, setExpandedNodes] = useState({}); // Atua como cache dos dados
    const [nodesVisibility, setNodesVisibility] = useState({}); // Controla apenas a visibilidade (aberto/fechado)
    const [loadingNodes, setLoadingNodes] = useState({}); // Controla o loading de cada nó individual
    const [expandedSns, setExpandedSns] = useState({}); // Controla a visibilidade dos detalhes do SN

    // Novos estados para carregamento faseado
    const [packagesLoaded, setPackagesLoaded] = useState(false);
    const [isLoadingPackages, setIsLoadingPackages] = useState(false);
    const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
    const { t } = useTranslation();

    // Simplificando o controle de requisição para evitar requisições iniciais duplicadas,
    // embora o uso do useRef para debounce em handleExpand possa ser mantido se necessário.
    const initialRequestDone = useRef(false);

    // 2. Função de Inicialização (Chamada de API principal - QUERY LEVE)
    const getInitialInfo = useCallback(async (codigo) => {
        if (initialRequestDone.current || !codigo) return;
        initialRequestDone.current = true;
        setIsLoadingInitial(true);

        const result = await fetchLiteInfo(codigo);
        if (result) {
            setValuesMap(result);
            setExpandedNodes(prev => ({ ...prev, [codigo]: result })); // Adiciona o nó principal ao cache
            setPackagesLoaded(false); // Make sure packages are not loaded yet
        }

        setIsLoadingInitial(false);
    }, []);

    // Função para carregar pacotes completos a pedido do utilizador
    const loadPackages = async (codigo) => {
        setIsLoadingPackages(true);
        const result = await fetchFullInfo(codigo);
        if (result) {
            setValuesMap(result);
            setExpandedNodes(prev => ({ ...prev, [codigo]: result }));
            setPackagesLoaded(true);
            setNodesVisibility(prev => ({ ...prev, rootPackages: true }));
        }
        setIsLoadingPackages(false);
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const codigo = urlParams.get("vol");
        getInitialInfo(codigo);
    }, [getInitialInfo]);

    // 3. Função de Expansão/Recolhimento (Chamada de API para sub-volumes)
    const handleExpand = async (vol) => {
        // Apenas alterna a visibilidade
        setNodesVisibility(prev => ({ ...prev, [vol]: !prev[vol] }));

        // Se os dados do volume ainda não estiverem no cache (expandedNodes), faz o pedido à API.
        // Isto só acontecerá na primeira vez que o nó for expandido.
        if (!expandedNodes[vol]) {
            setLoadingNodes(prev => ({ ...prev, [vol]: true })); // Ativa o loading para este nó

            const result = await fetchFullInfo(vol);
            if (result) {
                setExpandedNodes((prev) => ({
                    ...prev,
                    [vol]: result,
                }));
            }

            setLoadingNodes(prev => {
                const newLoadingNodes = { ...prev };
                delete newLoadingNodes[vol];
                return newLoadingNodes;
            }); // Desativa o loading para este nó
        }
    };

    // 3.5. Função para expandir/recolher detalhes do SN
    const handleSnClick = (snRecordName) => {
        setExpandedSns(prev => ({ ...prev, [snRecordName]: !prev[snRecordName] }));
    };

    // 3.6 Fazer download do CSV com toda a arvore do volume
    const handleDownloadCsv = async (volCodigo) => {
        setIsDownloadingCsv(true);
        try {
            const response = await axios.get(`${DOWNLOADVOLCSV}?vol=${volCodigo}`, {
                headers: {
                    "Content-Type": "application/json",
                    'Accept-Language': 'pt' // Podes ligar ao selectedLanguage do Redux futuramente se necessário
                },
                responseType: "blob",
            });

            if (response.status === 200) {
                const currentDate = new Date().toISOString().replace(/:/g, '-').slice(0, 19);
                const filename = `volume_${volCodigo}_${currentDate}.csv`;

                const blob = new Blob([response.data], { type: "application/octet-stream" });
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.download = filename;
                link.click();

                window.URL.revokeObjectURL(url);
            } else {
                throw new Error("Failed to get csv file");
            }
        } catch (error) {
            console.error("Error downloading csv file", error);
            toast.error(t("vol_csv_download"));
        } finally {
            setIsDownloadingCsv(false);
        }
    };


    // 4. Renderização
    return (
        <>
            {/* Usando o ModalWait apenas para o loading inicial */}
            <ModalWait show={isLoadingInitial} onHide={() => setIsLoadingInitial(false)} />

            <Container>
                {valuesMap ? (
                    <>
                        {/* Seção Principal do Volume */}
                        <div className="mt-2 mb-4 p-3" style={{ backgroundColor: "#007bff", color: "white", borderRadius: "5px" }}>
                            <h4 className="text-center mb-1">
                                {valuesMap.CodVolume || "N/A"}
                            </h4>
                            <h6 className="text-center mb-0">
                                {valuesMap.client || "N/A"} - {valuesMap.clientname || "Unknown Client"}
                            </h6>
                        </div>

                        <VolumeDetails volume={valuesMap} />

                        <hr className="my-4" />

                        {/* Seção de Volumes Filhos (Hierarquia) */}
                        <Row className="mt-4">
                            <Col sm={12}>
                                <div className="d-flex align-items-center mb-3">
                                    <div
                                        className="d-flex align-items-center"
                                        style={{ cursor: 'pointer', width: 'fit-content' }}
                                        onClick={async () => {
                                            if (!packagesLoaded) {
                                                await loadPackages(valuesMap.CodVolume);
                                            } else {
                                                setNodesVisibility(prev => ({ ...prev, rootPackages: !prev.rootPackages }));
                                            }
                                        }}
                                    >
                                        {isLoadingPackages ? (
                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                        ) : packagesLoaded && nodesVisibility.rootPackages !== false ? (
                                            <FaChevronDown size={24} className="me-2 text-primary" />
                                        ) : (
                                            <FaChevronRight size={24} className="me-2 text-primary" />
                                        )}
                                        <h4 className="mb-0" style={{
                                            fontWeight: packagesLoaded && nodesVisibility.rootPackages !== false ? 'bold' : 'normal',
                                            color: packagesLoaded && nodesVisibility.rootPackages !== false ? '#0056b3' : 'inherit',
                                        }}>
                                            📦 Packages
                                        </h4>
                                    </div>
                                    <button
                                        className="ml-3 btn btn-light border-0 shadow-sm"
                                        style={{ display: "flex", alignItems: "center", justifyContent: "center", marginLeft: "15px" }}
                                        onClick={() => handleDownloadCsv(valuesMap.CodVolume)}
                                        disabled={isDownloadingCsv}
                                        title="Download CSV"
                                    >
                                        {isDownloadingCsv ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : <FaDownload size={18} color="green" />}
                                    </button>
                                </div>

                                {packagesLoaded && nodesVisibility.rootPackages !== false && (
                                    <div style={{ marginLeft: '10px', borderLeft: '2px solid #ddd', paddingLeft: '15px' }}>
                                        <VolumeTree
                                            volume={valuesMap}
                                            expandedNodes={expandedNodes}
                                            handleExpand={handleExpand}
                                            nodesVisibility={nodesVisibility}
                                            loadingNodes={loadingNodes}
                                            handleSnClick={handleSnClick}
                                            expandedSns={expandedSns}
                                        />
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </>
                ) : (
                    // Mensagem enquanto carrega ou se não encontrar
                    !isLoadingInitial && (
                        <Row className="mt-5">
                            <Col className="text-center">
                                <p>{t("vol_not_found")}</p>
                            </Col>
                        </Row>
                    )
                )}
            </Container>
        </>
    );
}

export default PageQrCode;